// src/Pages/usuarios.jsx

import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import '../Marcas/Marcas.css'; // Reutilizando o CSS de Marcas/Empresas para consistência
import MenuLateral from "../Components/MenuPrincipal/MenuLateral";
import Swal from 'sweetalert2';
import { useAuth } from "../Context/auth";
import Pagination from "../Components/Pagination/Pagination";

// Helper para formatar data
const formatDate = (dateString) => {
    if (!dateString) return '-';
    // Converte UTC para local se necessário, ou apenas formata
    return new Date(dateString).toLocaleString('pt-BR');
};

// Componente de Grid local para simplificar
function GridUsuarios({ arrayUsuarios, clickDelete, userPermissions }) {
    const navigate = useNavigate();
    if (!arrayUsuarios || arrayUsuarios.length === 0) {
        return (
            <div className="empty-state text-center p-5">
                <div className="empty-icon mb-3" style={{ fontSize: '3rem', color: '#dee2e6' }}>
                    <i className="fas fa-user-friends"></i>
                </div>
                <h5 className="empty-title text-muted">Nenhum usuário encontrado</h5>
                <p className="text-muted small">
                    Não há usuários cadastrados neste tenant.
                </p>
            </div>
        );
    }

    return (
        <div className="marcas-grid-container">
            <table className="table-custom">
                <thead>
                    <tr>
                        <th className="text-center" style={{ width: '100px' }}>Ações</th>
                        <th className="text-start">Nome</th>
                        <th className="text-start">E-mail (Login)</th>
                        <th className="text-start">Data Cad.</th>
                        <th className="text-start">Usuário Cad.</th>
                        <th className="text-start">Data Alt.</th>
                        <th className="text-start">Usuário Alt.</th>
                    </tr>
                </thead>
                <tbody>
                    {arrayUsuarios.map((usuario) => (
                        <tr key={usuario.id}>
                            <td className="text-center" data-label="Ações">
                                <div className="actions-cell">
                                    <button
                                        onClick={() => {
                                            if (!userPermissions || !userPermissions['editar_usuarios']) {
                                                Swal.fire({
                                                    icon: 'error',
                                                    title: 'Acesso Negado',
                                                    text: 'Você não tem permissão para editar usuários.',
                                                    confirmButtonColor: '#2185c6'
                                                });
                                                return;
                                            }
                                            navigate(`/app/editarusuario/${usuario.id}`);
                                        }}
                                        className="btn-action-sm btn-edit"
                                        title="Editar"
                                    >
                                        <i className="fas fa-edit"></i>
                                    </button>
                                    <button onClick={() => clickDelete(usuario.id)} className="btn-action-sm btn-delete" title="Excluir">
                                        <i className="fas fa-trash"></i>
                                    </button>
                                </div>
                            </td>
                            <td data-label="Nome">
                                <div className="d-flex flex-column">
                                    <span className="fw-medium">{usuario.nome}</span>
                                </div>
                            </td>
                            <td data-label="E-mail (Login)">{usuario.email}</td>
                            <td data-label="Data Cadastro">{formatDate(usuario.data_criacao)}</td>
                            <td data-label="Usuário Cadastro">{usuario.usuario_criacao_nome || '-'}</td>
                            <td data-label="Data Últ. Alt.">{formatDate(usuario.data_ultima_alteracao)}</td>
                            <td data-label="Usuário Últ. Alt.">{usuario.usuario_ultima_alteracao_nome || '-'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function Usuarios() {
    const { currentUser, appUserData } = useAuth(); // Acesso às permissões
    const navigate = useNavigate();
    const [usuarios, setUsuarios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState('');
    const [trigger, setTrigger] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');

    // Paginação
    const [currentPage, setCurrentPage] = useState(1);
    const [itensPerPage, setItensPerPage] = useState(10);

    // Validação de Permissão de Leitura
    useEffect(() => {
        if (appUserData && !appUserData['ler_usuarios']) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Você não tem permissão para visualizar esta tela.',
                confirmButtonColor: '#2185c6'
            }).then(() => {
                navigate('/app/home');
            });
        }
    }, [appUserData, navigate]);

    useEffect(() => {
        const fetchUsuarios = async () => {
            if (!currentUser) return;
            setLoading(true);
            try {
                const token = await currentUser.getIdToken();
                const API_URL = process.env.REACT_APP_API_URL;
                const response = await fetch(`${API_URL}/api/usuarios`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!response.ok) throw new Error("Falha ao carregar usuários.");
                const data = await response.json();
                setUsuarios(data);
            } catch (error) {
                setErro('Ocorreu um erro ao carregar a lista de usuários.');
            } finally {
                setLoading(false);
            }
        };
        fetchUsuarios();
    }, [currentUser, trigger]);

    // Filtragem
    const filteredUsuarios = usuarios.filter(usuario =>
        usuario.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
        usuario.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Lógica de Paginação
    const indexOfLastItem = currentPage * itensPerPage;
    const indexOfFirstItem = indexOfLastItem - itensPerPage;
    const currentUsuarios = filteredUsuarios.slice(indexOfFirstItem, indexOfLastItem);

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
    const handleItemsPerPageChange = (newLimit) => {
        setItensPerPage(newLimit);
        setCurrentPage(1);
    };

    async function deleteUsuario(id) {
        if (!appUserData || !appUserData['excluir_usuarios']) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Você não tem permissão para excluir usuários.',
                confirmButtonColor: '#2185c6'
            });
            return;
        }

        const result = await Swal.fire({ title: "Você tem certeza?", text: "O usuário será excluído do Firebase e do banco de dados!", icon: "warning", showCancelButton: true, confirmButtonColor: "#2185c6", cancelButtonColor: "#DC3545", confirmButtonText: "Sim, excluir!", cancelButtonText: "Cancelar" });
        if (result.isConfirmed) {
            try {
                const token = await currentUser.getIdToken();
                const API_URL = process.env.REACT_APP_API_URL;
                const response = await fetch(`${API_URL}/api/usuarios/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error);
                }
                Swal.fire("Excluído!", "O usuário foi deletado com sucesso.", "success");
                setTrigger(Date.now());
            } catch (error) {
                Swal.fire("Erro!", error.message, "error");
            }
        }
    }

    if (loading) return (
        <div className="marcas-page-container" style={{ display: 'flex', paddingTop: 0 }}>
            <MenuLateral />
            <div className="content-with-sidebar d-flex justify-content-center align-items-center" style={{ width: '100%' }}>
                <div className="loading-spinner"></div>
                <p className="ms-2">Carregando...</p>
            </div>
        </div>
    );

    return (
        <div className="marcas-page-container" style={{ display: 'flex', paddingTop: 0 }}>
            <MenuLateral />

            <div className="content-with-sidebar">
                <div className="marcas-header">
                    <h1 className="marcas-title">
                        <i className="fas fa-users text-primary"></i>
                        Listagem de <span className="highlight">Usuários</span>
                    </h1>
                </div>

                <main className="marcas-content" style={{ maxWidth: '98%' }}>
                    <div className="marcas-controls">
                        <button
                            onClick={() => {
                                if (!appUserData || !appUserData['cadastrar_usuarios']) {
                                    Swal.fire({
                                        icon: 'error',
                                        title: 'Acesso Negado',
                                        text: 'Você não tem permissão para cadastrar usuários.',
                                        confirmButtonColor: '#2185c6'
                                    });
                                    return;
                                }
                                navigate('/app/novousuario');
                            }}
                            className="btn-primary-custom me-3"
                        >
                            <i className="fas fa-plus"></i>Novo Usuário
                        </button>

                        <div className="search-wrapper">
                            <i className="fas fa-search search-icon"></i>
                            <input
                                type="text"
                                className="search-input-custom"
                                placeholder="Pesquisar por nome ou e-mail..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <button className="btn-icon-custom ms-2" onClick={() => setTrigger(Date.now())} title="Atualizar Lista">
                            <i className="fas fa-sync-alt"></i>
                        </button>
                    </div>

                    {erro && <div className="alert alert-danger alert-custom" role="alert"><i className="fas fa-exclamation-triangle"></i>{erro}</div>}

                    <div className="table-wrapper">
                        <GridUsuarios arrayUsuarios={currentUsuarios} clickDelete={deleteUsuario} userPermissions={appUserData} />
                    </div>

                    <Pagination
                        totalItems={filteredUsuarios.length}
                        itemsPerPage={itensPerPage}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                        onItemsPerPageChange={handleItemsPerPageChange}
                    />
                </main>
            </div>
        </div>
    );
}
export default Usuarios;